// # User Review Submission Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { db } from '../../auth/firebase';
import { doc, runTransaction, updateDoc } from 'firebase/firestore';
import { generateReviewId } from '../../utils/idGenerator';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './user_review.css';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import cacheManager from '../../auth/cache_manager';

// # Product options with standardized IDs and display names
const PRODUCTS = [
  { id: 'step1_library', name: 'USMLE Step 1 Library' },
  { id: 'step2_library', name: 'USMLE Step 2 Library' },
  { id: 'step1_mentorship', name: 'USMLE Step 1 Mentorship' },
  { id: 'step2_mentorship', name: 'USMLE Step 2 Mentorship' },
  { id: 'oet', name: 'OET' },
  { id: 'us_visa_counselling', name: 'US Visa Counselling' },
];

// # Helper function to format product name for display
export const formatProductName = (productId) => {
  const product = PRODUCTS.find(p => p.id === productId);
  return product ? product.name : productId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

function UserReview() {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [review, setReview] = useState('');
  const [product, setProduct] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const functions = getFunctions(undefined, 'europe-west1');
  const createReviewNotification = httpsCallable(functions, 'createReviewNotification');

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!review.trim() || !product) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const year = new Date().getFullYear().toString();
      const reviewId = generateReviewId();
      const timestamp = new Date().toISOString();
      
      // # Get reference to the year document
      const yearDocRef = doc(db, 'reviews', year);

      // # Use transaction to update or create the year document
      await runTransaction(db, async (transaction) => {
        const yearDoc = await transaction.get(yearDocRef);
        const reviewData = {
          content: review.trim(),
          product,
          status: 'pending',
          userId: userProfile.uid,
          userName: `${userProfile.firstName} ${userProfile.lastName}`,
          timestamp,
          metadata: {
            createdAt: timestamp,
            lastModified: timestamp
          }
        };

        if (!yearDoc.exists()) {
          // # Create new year document if it doesn't exist
          transaction.set(yearDocRef, {
            reviews: {
              [reviewId]: reviewData
            },
            stats: {
              totalReviews: 1,
              pendingReviews: 1,
              acceptedReviews: 0,
              rejectedReviews: 0,
              lastUpdated: timestamp
            }
          });
        } else {
          // # Update existing year document
          const currentData = yearDoc.data();
          const reviews = currentData.reviews || {};
          const stats = currentData.stats || {
            totalReviews: 0,
            pendingReviews: 0,
            acceptedReviews: 0,
            rejectedReviews: 0
          };

          reviews[reviewId] = reviewData;
          
          transaction.update(yearDocRef, {
            reviews,
            stats: {
              ...stats,
              totalReviews: stats.totalReviews + 1,
              pendingReviews: stats.pendingReviews + 1,
              lastUpdated: timestamp
            }
          });
        }

        // # Create notification for admins
        await createReviewNotification({
          reviewData: {
            reviewId,
            content: review.trim(),
            product,
            userName: `${userProfile.firstName} ${userProfile.lastName}`,
            email: userProfile.email
          }
        });
      });

      navigate('/reviews', { state: { message: 'Review submitted successfully!' } });
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`usr_rvw_page-container ${isDarkMode ? 'usr_rvw_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="usr_rvw_user-review-container">
        <div className="usr_rvw_review-form-wrapper">
          <h1>Write a Review</h1>
          <p className="usr_rvw_review-subtitle">Share your experience with our products</p>

          {error && <div className="usr_rvw_error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="usr_rvw_review-form">
            <div className="usr_rvw_form-group">
              <label htmlFor="product">Select Product</label>
              <select
                id="product"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                required
              >
                <option value="">Choose a product...</option>
                {PRODUCTS.map((prod) => (
                  <option key={prod.id} value={prod.id}>
                    {prod.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="usr_rvw_form-group">
              <label htmlFor="review">Your Review</label>
              <textarea
                id="review"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Share your experience..."
                required
                rows={6}
              />
            </div>

            <div className="usr_rvw_form-actions">
              <button 
                type="button" 
                className="usr_rvw_cancel-button"
                onClick={() => navigate('/reviews')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="usr_rvw_submit-button" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default UserReview; 